import { authenticatedFetch } from "./http";

export type InventoryCountMode = "HU_ONLY" | "CONTENT_ONLY" | "HU_AND_CONTENT";
export type InventoryCountScopeMode = "WAREHOUSE" | "SELECTED_LOCATIONS";
export type InventoryCountSelectionMethod = "GENERAL" | "TARGETED" | "ABC";
export type InventoryCountStatus =
  "DRAFT" | "READY" | "IN_PROGRESS" | "TO_RESOLVE" | "PENDING_APPROVAL" | "COMPLETED" | "CANCELLED";

export interface InventoryCountScope {
  locationType: "STORAGE_AREA" | "STORAGE_BIN" | "PROCESS_POINT";
  locationId: string;
}

export interface InventoryCountLocation {
  locationType: "STORAGE_BIN" | "PROCESS_POINT";
  locationId: string;
  code: string;
  displayName: string | null;
}

export interface InventoryCountCampaignMutation {
  warehouseId: string;
  clientId: string | null;
  displayName: string;
  countMode: InventoryCountMode;
  scopeMode: InventoryCountScopeMode;
  selectionMethod: InventoryCountSelectionMethod;
  abcClasses: Array<"A" | "B" | "C">;
  blindCount: boolean;
  countDeadline: string | null;
  assignedUserId: string | null;
  scopes: InventoryCountScope[];
  version: number | null;
}

export interface InventoryCountPreview {
  locationCount: number;
  emptyLocationCount: number;
  handlingUnitCount: number;
  contentLineCount: number;
  unlocatedHandlingUnitCount: number;
}

export interface InventoryAbcPreview {
  materialCount: number;
  locationCount: number;
  unclassifiedMaterialCount: number;
  classAFrequencyDays: number;
  classBFrequencyDays: number;
  classCFrequencyDays: number;
  materials: Array<{
    itemId: string; itemCode: string; displayName: string; abcClass: "A" | "B" | "C";
    lastCountedOn: string | null; nextCountDueOn: string | null; overdue: boolean; locationCount: number;
  }>;
  notDueMaterials: Array<{
    itemId: string; itemCode: string; displayName: string; abcClass: "A" | "B" | "C";
    lastCountedOn: string | null; nextCountDueOn: string | null; overdue: boolean; locationCount: number;
  }>;
  scopes: InventoryCountScope[];
}

export interface InventoryAbcPolicy {
  organizationId: string;
  classAFrequencyDays: number;
  classBFrequencyDays: number;
  classCFrequencyDays: number;
  updatedAt: string | null;
}

export function loadInventoryAbcPolicy(warehouseId: string): Promise<InventoryAbcPolicy> {
  return authenticatedFetch<InventoryAbcPolicy>(
    `/api/v1/inventory-counts/abc/policy?warehouseId=${encodeURIComponent(warehouseId)}`
  );
}

export function saveInventoryAbcPolicy(
  warehouseId: string,
  policy: Pick<InventoryAbcPolicy,
    "classAFrequencyDays" | "classBFrequencyDays" | "classCFrequencyDays">
): Promise<InventoryAbcPolicy> {
  return authenticatedFetch<InventoryAbcPolicy>("/api/v1/inventory-counts/abc/policy", {
    method: "PUT", body: JSON.stringify({ warehouseId, ...policy })
  });
}

export interface InventoryCountCampaign {
  id: string;
  campaignNumber: string;
  displayName: string;
  warehouseId: string;
  warehouseCode: string;
  warehouseName: string;
  clientId: string | null;
  clientCode: string | null;
  clientName: string | null;
  countMode: InventoryCountMode;
  scopeMode: InventoryCountScopeMode;
  selectionMethod: InventoryCountSelectionMethod;
  abcClasses: Array<"A" | "B" | "C">;
  blindCount: boolean;
  status: InventoryCountStatus;
  countDeadline: string | null;
  snapshotAt: string | null;
  locationCount: number;
  handlingUnitCount: number;
  contentLineCount: number;
  version: number;
  completedAt: string | null;
  completedBy: string | null;
  completedLocationCount: number;
  discrepancyLocationCount: number;
  huDiscrepancyCount: number;
  contentDiscrepancyCount: number;
  cancelledAt: string | null;
  cancelledBy: string | null;
  cancellationReason: string | null;
  decisions: InventoryCountDecision[];
  investigations: InventoryCountInvestigation[];
  scopes: InventoryCountScope[];
  locations: InventoryCountLocation[];
}

export interface InventoryCountFinalReviewLine {
  locationCode: string;
  huNumber: string;
  itemCode: string;
  lotNumber: string | null;
  serialNumber: string | null;
  theoreticalQuantity: number;
  observedQuantity: number;
  targetQuantity: number;
  uom: string;
  decision: string | null;
  causeCode: InventoryCountDiscrepancyCause | null;
  reason: string | null;
  decidedAt: string | null;
  decidedBy: string | null;
}

export interface InventoryCountFinalReview {
  campaignId: string;
  campaignNumber: string;
  displayName: string;
  locationCount: number;
  handlingUnitCount: number;
  contentLineCount: number;
  adjustmentCount: number;
  lines: InventoryCountFinalReviewLine[];
}

export interface InventoryCountInvestigation {
  id: string;
  actionNumber: string | null;
  taskId: string;
  investigationType: "MISSING_HU" | "UNKNOWN_HU";
  huNumber: string;
  locationCode: string;
  status: "OPEN" | "IN_PROGRESS" | "RESOLVED" | "CANCELLED";
  reason: string;
  createdAt: string;
  createdBy: string;
  assignedAt: string | null;
  assignedTo: string | null;
  resolutionDecision: "FOUND_AT_EXPECTED" | "FOUND_ELSEWHERE" | "DECLARE_LOST" |
    "SCAN_ERROR" | "QUARANTINED_UNKNOWN" | null;
  resolutionLocationCode: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  resolutionComment: string | null;
}

export interface StockRemediation {
  investigationId: string;
  actionNumber: string;
  campaignNumber: string;
  investigationType: "MISSING_HU" | "UNKNOWN_HU";
  status: "DETECTED" | "OPEN" | "IN_PROGRESS";
  handlingUnitNumber: string;
  itemSummary: string | null;
  warehouseId: string;
  warehouseCode: string;
  clientCode: string | null;
  clientName: string | null;
  expectedLocationCode: string;
  currentLocationCode: string | null;
  reason: string;
  createdAt: string;
  assignedTo: string | null;
}

export interface InventoryCountDecision {
  locationCode: string;
  action: "RECOUNT_REQUESTED" | "SCAN_ERROR_REMOVED" | "MISSING_HU_SEARCH_REQUESTED" |
    "UNKNOWN_HU_INVESTIGATION_OPENED" | "HU_LOCATION_REGULARIZED" |
    "CONTENT_OBSERVATION_CORRECTED" | "CONTENT_STOCK_ADJUSTED" |
    "CONTENT_INVESTIGATION_OPENED";
  huNumber: string | null;
  causeCode: InventoryCountDiscrepancyCause | null;
  reason: string;
  performedAt: string;
  performedBy: string;
  recountTargets: InventoryCountRecountTarget[];
}

export interface InventoryCountRecountTarget {
  targetType: "HU" | "CONTENT";
  huNumber: string;
  itemCode: string | null;
  lotNumber: string | null;
  serialNumber: string | null;
  expectedQuantity: number | null;
  observedQuantity: number | null;
  uom: string | null;
}

export interface InventoryCountTask {
  id: string;
  campaignId: string;
  sequenceNumber: number;
  status: "READY" | "IN_PROGRESS" | "SUBMITTED" | "TO_RESOLVE" | "PENDING_APPROVAL" | "COMPLETED" | "CANCELLED";
  locationType: "STORAGE_BIN" | "PROCESS_POINT";
  locationCode: string;
  locationName: string | null;
  countMode: InventoryCountMode;
  blindCount: boolean;
  expectedHuCount: number | null;
  expectedHuNumbers: string[];
  locationConfirmed: boolean;
  observedHuNumbers: string[];
  matchingHuNumbers: string[];
  missingHuNumbers: string[];
  unexpectedHuNumbers: string[];
  missingHus: InventoryCountDiscrepancyHu[];
  unexpectedHus: InventoryCountDiscrepancyHu[];
  contentConfirmedHuNumbers: string[];
  contentLines: InventoryCountContentLine[];
  version: number;
}

export interface InventoryCountContentLine {
  snapshotId: string | null;
  huNumber: string;
  itemCode: string;
  itemName: string;
  lotNumber: string | null;
  serialNumber: string | null;
  expectedQuantity: number | null;
  observedQuantity: number | null;
  uom: string;
  unexpected: boolean;
  discrepancy: boolean;
  remediationStatus: "OPEN" | "PENDING_APPROVAL" | "COMPLETED" | null;
  remediationDecision: "OBSERVATION_CORRECTED" | "STOCK_ADJUSTED" | "INVESTIGATION_OPENED" | null;
  remediationCauseCode: InventoryCountDiscrepancyCause | null;
}

export interface InventoryCountDiscrepancyHu {
  huNumber: string;
  known: boolean;
  currentLocationCode: string | null;
}

export function loadInventoryCountCampaigns(): Promise<InventoryCountCampaign[]> {
  return authenticatedFetch<InventoryCountCampaign[]>("/api/v1/inventory-counts");
}

export function previewInventoryCount(
  request: InventoryCountCampaignMutation
): Promise<InventoryCountPreview> {
  return authenticatedFetch<InventoryCountPreview>("/api/v1/inventory-counts/preview", {
    method: "POST", body: JSON.stringify(request)
  });
}

export function previewAbcInventoryCount(request: {
  warehouseId: string; clientId: string; classes: Array<"A" | "B" | "C">;
  dueThrough: string; maxMaterials?: number;
}): Promise<InventoryAbcPreview> {
  return authenticatedFetch<InventoryAbcPreview>("/api/v1/inventory-counts/abc/preview", {
    method: "POST", body: JSON.stringify(request)
  });
}

export function createInventoryCountCampaign(
  request: InventoryCountCampaignMutation
): Promise<InventoryCountCampaign> {
  return authenticatedFetch<InventoryCountCampaign>("/api/v1/inventory-counts", {
    method: "POST", body: JSON.stringify(request)
  });
}

export function updateInventoryCountCampaign(
  id: string, request: InventoryCountCampaignMutation
): Promise<InventoryCountCampaign> {
  return authenticatedFetch<InventoryCountCampaign>(`/api/v1/inventory-counts/${id}`, {
    method: "PUT", body: JSON.stringify(request)
  });
}

export function prepareInventoryCountCampaign(
  campaign: InventoryCountCampaign
): Promise<InventoryCountCampaign> {
  return authenticatedFetch<InventoryCountCampaign>(
    `/api/v1/inventory-counts/${campaign.id}/prepare?version=${campaign.version}`,
    { method: "POST" }
  );
}

export function cancelInventoryCountCampaign(
  campaign: InventoryCountCampaign, reason: string
): Promise<InventoryCountCampaign> {
  return authenticatedFetch<InventoryCountCampaign>(
    `/api/v1/inventory-counts/${campaign.id}/cancel`,
    { method: "POST", body: JSON.stringify({ version: campaign.version, reason }) }
  );
}

export function loadInventoryCountFinalReview(id: string): Promise<InventoryCountFinalReview> {
  return authenticatedFetch<InventoryCountFinalReview>(`/api/v1/inventory-counts/${id}/final-review`);
}

export function approveInventoryCountCampaign(
  campaign: InventoryCountCampaign, reason: string
): Promise<InventoryCountCampaign> {
  return authenticatedFetch<InventoryCountCampaign>(`/api/v1/inventory-counts/${campaign.id}/approve`, {
    method: "POST", body: JSON.stringify({ version: campaign.version, reason })
  });
}

export function loadInventoryCountTasks(campaignId: string): Promise<InventoryCountTask[]> {
  return authenticatedFetch<InventoryCountTask[]>(`/api/v1/inventory-counts/${campaignId}/tasks`);
}

export function startInventoryCountTask(taskId: string, locationCode: string): Promise<InventoryCountTask> {
  return authenticatedFetch<InventoryCountTask>(`/api/v1/inventory-counts/tasks/${taskId}/start`, {
    method: "POST", body: JSON.stringify({ locationCode })
  });
}

export function observeInventoryCountHu(taskId: string, huNumber: string): Promise<InventoryCountTask> {
  return authenticatedFetch<InventoryCountTask>(`/api/v1/inventory-counts/tasks/${taskId}/observations/hu`, {
    method: "POST", body: JSON.stringify({ huNumber })
  });
}

export function observeInventoryCountContent(
  taskId: string,
  request: {
    huNumber: string; snapshotId: string | null; itemCode: string;
    lotNumber: string | null; serialNumber: string | null;
    quantity: number; uom: string;
  }
): Promise<InventoryCountTask> {
  return authenticatedFetch<InventoryCountTask>(`/api/v1/inventory-counts/tasks/${taskId}/observations/content`, {
    method: "POST", body: JSON.stringify(request)
  });
}

export function confirmInventoryCountContent(
  taskId: string, huNumber: string
): Promise<InventoryCountTask> {
  return authenticatedFetch<InventoryCountTask>(`/api/v1/inventory-counts/tasks/${taskId}/observations/content/confirm`, {
    method: "POST", body: JSON.stringify({ huNumber })
  });
}

export function submitInventoryCountTask(task: InventoryCountTask, emptyConfirmed: boolean): Promise<InventoryCountTask> {
  return authenticatedFetch<InventoryCountTask>(`/api/v1/inventory-counts/tasks/${task.id}/submit`, {
    method: "POST", body: JSON.stringify({ emptyConfirmed, version: task.version })
  });
}

export function recountInventoryCountTask(taskId: string, comment = ""): Promise<InventoryCountTask> {
  return authenticatedFetch<InventoryCountTask>(`/api/v1/inventory-counts/tasks/${taskId}/recount`, {
    method: "POST", body: JSON.stringify({ reason: comment })
  });
}

export function removeInventoryCountObservation(
  taskId: string, huNumber: string, causeCode: InventoryCountDiscrepancyCause, reason: string
): Promise<InventoryCountTask> {
  return authenticatedFetch<InventoryCountTask>(`/api/v1/inventory-counts/tasks/${taskId}/observations/hu/remove`, {
    method: "POST", body: JSON.stringify({ huNumber, causeCode, reason })
  });
}

export function resolveInventoryCountDiscrepancy(
  taskId: string, huNumber: string,
  decision: "SEARCH_MISSING" | "INVESTIGATE_UNKNOWN" | "REGULARIZE_LOCATION",
  causeCode: InventoryCountDiscrepancyCause, reason: string
): Promise<InventoryCountTask> {
  return authenticatedFetch<InventoryCountTask>(`/api/v1/inventory-counts/tasks/${taskId}/resolve`, {
    method: "POST", body: JSON.stringify({ huNumber, decision, causeCode, reason })
  });
}

export type InventoryCountDiscrepancyCause =
  "COUNT_ERROR" | "DATA_ENTRY_ERROR" | "LOSS" | "DAMAGE" |
  "UNDECLARED_CONSUMPTION" | "MISPLACED_STOCK" | "FOUND_SURPLUS" |
  "PROCESS_ERROR" | "OTHER";

export function remediateInventoryCountContent(
  taskId: string, snapshotId: string,
  decision: "OBSERVATION_CORRECTED" | "STOCK_ADJUSTED" | "INVESTIGATION_OPENED",
  causeCode: InventoryCountDiscrepancyCause, reason: string
): Promise<InventoryCountTask> {
  const action = decision === "STOCK_ADJUSTED" ? "adjust" : "remediate";
  return authenticatedFetch<InventoryCountTask>(
    `/api/v1/inventory-counts/tasks/${taskId}/content/${action}`, {
      method: "POST", body: JSON.stringify({ snapshotId, decision, causeCode, reason })
    }
  );
}

export function startInventoryCountInvestigation(id: string): Promise<InventoryCountInvestigation> {
  return authenticatedFetch<InventoryCountInvestigation>(`/api/v1/inventory-counts/investigations/${id}/start`, {
    method: "POST"
  });
}

export function resolveInventoryCountInvestigation(
  id: string,
  decision: NonNullable<InventoryCountInvestigation["resolutionDecision"]>,
  locationCode: string | null,
  reason: string
): Promise<InventoryCountInvestigation> {
  return authenticatedFetch<InventoryCountInvestigation>(`/api/v1/inventory-counts/investigations/${id}/resolve`, {
    method: "POST",
    body: JSON.stringify({ decision, locationCode, reason })
  });
}
